import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Header } from 'components/Header';
import { useAuth } from 'context/AuthContext';
import { 
  FaUser, 
  FaEnvelope, 
  FaTrophy, 
  FaChartLine,
  FaCamera
} from 'react-icons/fa';
import { TrendingUp, ArrowLeft } from 'lucide-react';
import Avatar from 'components/ui/avatar';
import AvatarSelector from 'components/ui/avatar-selector';
import UserProgressChart, { ProgressDataPoint } from 'components/ui/user-progress-chart';
import { 
  getPlayerStatus, 
  getLeaderboardsByPlayer,
  generateProgressData,
  getPlayerPositionInLeaderboard
} from 'services/ranking.service';
import { updateUserOnboarding } from 'services/auth.service';
import { fetchUser } from 'services/auth.service';
import { getProfileImage } from 'utils/helpers';
import { User } from 'types/user';
import { LeaderboardResponse } from 'types/ranking';
import './styles.scss';

interface PlayerRankingData {
  leaderboardId: string;
  leaderboardTitle: string;
  position: number;
  points: number;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedAvatar, setSelectedAvatar] = useState<string>(
    user?.extra?.fotoDePerfil || ''
  );
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [playerStats, setPlayerStats] = useState<User | null>(null);
  const [playerRankings, setPlayerRankings] = useState<PlayerRankingData[]>([]);
  const [progressData, setProgressData] = useState<ProgressDataPoint[]>([]);

  // Busca dados completos do player e seus rankings
  useEffect(() => {
    const fetchPlayerData = async () => {
      if (!user?._id && !user?.email) {
        setLoadingData(false);
        return;
      }

      setLoadingData(true);
      try {
        // 1. Busca status do jogador
        const stats = await getPlayerStatus();
        setPlayerStats(stats);

        // 2. Busca leaderboards do jogador
        const leaderboards = await getLeaderboardsByPlayer(
          user._id,
          user.email
        );

        // 3. Para cada leaderboard, busca a posição do jogador
        const rankingsData: PlayerRankingData[] = [];
        
        for (const leaderboard of leaderboards) {
          const positionData = await getPlayerPositionInLeaderboard(
            leaderboard._id,
            user.email || user._id
          );

          if (positionData) {
            rankingsData.push({
              leaderboardId: leaderboard._id,
              leaderboardTitle: positionData.leaderboardTitle,
              position: positionData.position,
              points: positionData.points,
            });
          }
        }

        setPlayerRankings(rankingsData);

        // 4. Gera dados de progresso baseado no primeiro ranking (para o gráfico geral)
        if (rankingsData.length > 0) {
          const firstRanking = rankingsData[0];
          const progress = generateProgressData(
            firstRanking.points,
            firstRanking.position
          );
          setProgressData(progress);
        } else if (stats.total_points) {
          // Fallback: usa pontos totais se não houver rankings
          const progress = generateProgressData(
            stats.total_points,
            stats.positions?.[0]?.position || 0
          );
          setProgressData(progress);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do jogador:', error);
        toast.error('Erro ao carregar dados do perfil', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      fetchPlayerData();
    }
  }, [user]);

  const handleAvatarSelect = (avatarName: string) => {
    setSelectedAvatar(avatarName);
  };

  const handleSaveAvatar = async () => {
    if (!selectedAvatar) {
      toast.error('Selecione um avatar para continuar', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      await updateUserOnboarding(selectedAvatar, user._id);
      
      // Atualiza os dados do usuário localmente sem fazer PUT em status
      const updatedUser = await fetchUser();
      
      // Atualiza o estado local do player stats
      if (updatedUser) {
        setPlayerStats(updatedUser);
      }

      toast.success('Seu avatar foi alterado com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
      });

      setIsEditingAvatar(false);
      
      // Recarrega a página para atualizar o contexto e o header
      window.location.reload();
    } catch (error) {
      console.error('Erro ao atualizar avatar:', error);
      toast.error('Não foi possível atualizar o avatar. Tente novamente.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Calcula totais gerais (soma de todos os rankings)
  const totalPoints = playerRankings.reduce((sum, ranking) => sum + ranking.points, 0) || playerStats?.total_points || 0;
  const bestRank = playerRankings.length > 0 
    ? Math.min(...playerRankings.map(r => r.position))
    : playerStats?.positions?.[0]?.position || 0;

  // Loading state
  if (loadingData) {
    return (
      <div className="profile-page">
        <Header />
        <div className="profile-container">
          <div className="profile-content" style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: '#ffffff', fontSize: '1.2rem' }}>Carregando perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Header />
      
      <div className="profile-container">
        <div className="profile-content">
          {/* Header com Avatar e Info Principal */}
          <div className="profile-header">
            <div className="profile-avatar-section">
              <div className="profile-avatar-wrapper">
                <Avatar 
                  src={getProfileImage(user)}
                  name={user?.name}
                  size="2xl"
                  border
                />
                <button 
                  className="avatar-edit-button"
                  onClick={() => setIsEditingAvatar(!isEditingAvatar)}
                  title="Alterar avatar"
                >
                  <FaCamera />
                </button>
              </div>
            </div>

            <div className="profile-info">
              <h1>{user?.name || 'Usuário'}</h1>
              <p className="profile-email">{user?.email || user?._id || 'ID não disponível'}</p>
              
              {/* Resumo Geral */}
              {playerRankings.length > 0 && (
                <div className="profile-stats-summary">
                  <div className="stat-item-summary">
                    <FaTrophy className="stat-icon" />
                    <div className="stat-info">
                      <span className="stat-number">
                        {bestRank > 0 ? `#${bestRank}` : '-'}
                      </span>
                      <span className="stat-label">Melhor Posição</span>
                    </div>
                  </div>
                  
                  <div className="stat-item-summary">
                    <FaChartLine className="stat-icon" />
                    <div className="stat-info">
                      <span className="stat-number">{totalPoints}</span>
                      <span className="stat-label">Pontos Totais</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Seletor de Avatar (condicional) */}
          {isEditingAvatar && (
            <div className="profile-section">
              <AvatarSelector
                selectedAvatar={selectedAvatar}
                onSelect={handleAvatarSelect}
                onSave={handleSaveAvatar}
                loading={loading}
              />
            </div>
          )}

          {/* Seção de Rankings Individuais */}
          {playerRankings.length > 0 && (
            <div className="profile-section rankings-section">
              <h2 className="section-title">
                <FaTrophy size={20} />
                <span>Seus Rankings</span>
              </h2>
              <div className="rankings-grid">
                {playerRankings.map((ranking) => (
                  <div key={ranking.leaderboardId} className="ranking-card">
                    <div className="ranking-card-header">
                      <h3 className="ranking-title">{ranking.leaderboardTitle}</h3>
                    </div>
                    <div className="ranking-card-stats">
                      <div className="ranking-stat">
                        <span className="ranking-stat-label">Posição</span>
                        <span className="ranking-stat-value position">
                          {ranking.position > 0 ? `#${ranking.position}` : '-'}
                        </span>
                      </div>
                      <div className="ranking-stat-divider"></div>
                      <div className="ranking-stat">
                        <span className="ranking-stat-label">Pontuação</span>
                        <span className="ranking-stat-value points">
                          {ranking.points.toLocaleString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default Profile;
